import cities from "../../../constants/cities";
import { isAirportPickup } from "./utils";

const DEFAULT_LOCALE = "en-PK";

export const getPlaceLabel = (location) => {
  if (!location?.name) {
    return "";
  }
  if (typeof location.name === "object") {
    return location.name.label || "";
  }
  return location.name;
};

export const getCityNameFromPlace = (location) => {
  const place = location?.name;
  if (!place || typeof place !== "object") {
    return "";
  }

  const terms = place.value?.terms;
  if (terms?.length >= 2) {
    return terms[terms.length - 2].value;
  }

  const secondary = place.value?.structured_formatting?.secondary_text;
  if (secondary) {
    const parts = secondary.split(",").map((part) => part.trim());
    if (parts.length >= 2) {
      return parts[parts.length - 2] || parts[0];
    }
    return parts[0] || "";
  }

  if (place.label) {
    const parts = place.label.split(",").map((part) => part.trim());
    if (parts.length >= 2) {
      return parts[parts.length - 2];
    }
  }

  return "";
};

export const resolveCityIdByName = (cityName) => {
  if (!cityName) {
    return null;
  }

  const normalized = cityName.trim().toLowerCase();
  const match = cities.find(
    (city) => city.Name.toLowerCase() === normalized
  );

  return match?.Id ?? null;
};

export const getAirportCodeFromPlace = (location) => {
  const label = getPlaceLabel(location);
  if (!label) {
    return "";
  }

  const parenMatch = label.match(/\(([A-Z]{3})\)/);
  if (parenMatch) {
    return parenMatch[1];
  }

  if (/schiphol|amsterdam airport/i.test(label)) {
    return "AMS";
  }
  if (/heathrow/i.test(label)) {
    return "LHR";
  }
  if (/gatwick/i.test(label)) {
    return "LGW";
  }
  if (/inverness airport/i.test(label)) {
    return "INV";
  }

  const airportMatch = label.toUpperCase().match(/\b([A-Z]{3})\s+AIRPORT\b/);
  if (airportMatch) {
    return airportMatch[1];
  }

  return "";
};

export const buildTransferRatesBody = (formValues) => {
  const airportPickup = isAirportPickup(formValues.tripType);
  const destinationPlace = airportPickup ? formValues.dropoff : formValues.pickup;
  const airportPlace = airportPickup ? formValues.pickup : formValues.dropoff;

  const destinationName = getPlaceLabel(destinationPlace);
  const cityName =
    getCityNameFromPlace(destinationPlace) ||
    getCityNameFromPlace(airportPlace);
  const cityId = resolveCityIdByName(cityName);
  const searchKey = cityName ? cityName.slice(0, 3).toLowerCase() : "";
  const airportCode = getAirportCodeFromPlace(airportPlace);

  const shared = {
    cityId,
    searchKey,
    destinationName,
    airportCode,
    adults: parseInt(formValues.passengerCount, 10) || 1,
    currency: formValues.currency,
    locale: DEFAULT_LOCALE,
  };

  if (airportPickup) {
    return {
      flightNo: formValues.flightNumber.trim().toUpperCase(),
      departureDate: formValues.date,
      ...shared,
    };
  }

  return {
    rideType: "to_airport",
    pickupDateTime: formValues.dateTime.replace("T", " ").slice(0, 16),
    ...shared,
  };
};

export const validateTransferRatesBody = (body, tripType) => {
  const errors = {};
  const airportPickup = isAirportPickup(tripType);

  if (!body.cityId) {
    if (airportPickup) {
      errors.dropoff =
        "Could not resolve city from drop-off location. Select a place from Google suggestions.";
    } else {
      errors.pickup =
        "Could not resolve city from pickup location. Select a place from Google suggestions.";
    }
  }

  if (!body.airportCode) {
    if (airportPickup) {
      errors.pickup =
        "Could not resolve airport code from pickup. Select an airport or include IATA code (e.g. AMS).";
    } else {
      errors.dropoff =
        "Could not resolve airport code from drop-off. Select an airport or include IATA code (e.g. AMS).";
    }
  }

  return errors;
};
